import { AnyAction } from 'redux';
import { App, Produkt, Produktlinie, VhvV2Vorbelegungdaten } from '@ps20-frontend/frontend-models';
import { produktReducer } from '@ps20-frontend/reducers';
import { GlobalActions, VorbelegungPayload } from '@ps20-frontend/shop-integration-redux';
import { FahrradWert, Produktlinienname, TaasTarifrisikoschluesselVHVV2 } from '@ps20/bff-models';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';
import { selectIsErsatzgeschaeft } from '../../app/shared/selectors/vertragsoption.selector';
import {
  isFahrradBaustein,
  ZUSATZOPTION_FAHRRAD_ID,
  ZUSATZOPTION_FAHRRAD_KASKO_ID,
} from '../model/fahradwertForLinie.model';

export const vhvProduktReducer = function (
  sectionState: Produkt = null,
  action: AnyAction,
  state: IAppState,
): Produkt {
  const newSectionState: Produkt = produktReducer(sectionState, action, App.VHV_V2, state?.angebotsId);

  switch (action.type) {
    case VhvActions.BRUTTOPREISE_CALCULATED:
    case VhvActions.PRODUKTLINIEN_CHANGED: {
      const produktlinien: Produktlinie[] = action.payload;
      return { ...newSectionState, produktlinien };
    }
    case VhvActions.PRODUKT_ZUSATZOPTIONEN_VORBELEGT: {
      const relevantVorbelegungdaten: VhvV2Vorbelegungdaten = action.payload;
      const produktlinien: Produktlinie[] = newSectionState?.produktlinien;
      const newProduktLinien = produktlinien?.map(produktlinie => {
        if (!produktlinie.zusatzoptionen) {
          return produktlinie;
        }
        return {
          ...produktlinie,
          zusatzoptionen: produktlinie.zusatzoptionen.map(zusatzoption => {
            if (relevantVorbelegungdaten?.vorvertragBausteinList) {
              const found = relevantVorbelegungdaten.vorvertragBausteinList.find(zo => {
                if (zo?.taasTarifrisikoschluessel === zusatzoption?.name) {
                  return true;
                }
                return isFahrradBaustein(zo) && zusatzoption?.name === TaasTarifrisikoschluesselVHVV2.FAHRRAD_KASKO;
              });
              if (found) {
                return {
                  ...zusatzoption,
                  isSelected: true,
                  eigenschaft: isFahrradBaustein(found)
                    ? relevantVorbelegungdaten.fahrradWert
                    : zusatzoption.eigenschaft,
                };
              }
            }
            return zusatzoption;
          }),
        };
      });
      return { ...newSectionState, produktlinien: newProduktLinien };
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const vorbelegungdaten: VhvV2Vorbelegungdaten = action.payload;
      if (vorbelegungdaten.forApp !== App.VHV_V2 || vorbelegungdaten.angebotsId !== state?.angebotsId) {
        return newSectionState;
      }
      const fahrradBaustein = vorbelegungdaten.vorvertragBausteinList?.find(isFahrradBaustein);
      if (!fahrradBaustein) {
        return newSectionState;
      }
      const newProduktLinien = newSectionState?.produktlinien?.map(produktlinie => {
        if (!produktlinie.zusatzoptionen) {
          return produktlinie;
        }
        return {
          ...produktlinie,
          zusatzoptionen: produktlinie.zusatzoptionen.map(zusatzoption => {
            if (zusatzoption.id === ZUSATZOPTION_FAHRRAD_KASKO_ID) {
              return { ...zusatzoption, isSelected: true };
            }
            return zusatzoption;
          }),
        };
      });
      return { ...newSectionState, produktlinien: newProduktLinien };
    }
    // One-time override of the initial (backend-configured) KOMFORT default, applied as
    // soon as the Vorvertrag data is known. resetToDefaultProduktlinieIfNecessary
    // (produktlinienSaga.ts) is the safety net for when PREMIUM later becomes unavailable
    // or gets reactivated during the flow.
    case GlobalActions.VORBELEGUNG_FINISHED: {
      const actionPayload: VorbelegungPayload = action.payload;
      const vorhandeneVorbelegungdaten: VhvV2Vorbelegungdaten =
        actionPayload.vorhandeneVorbelegungdaten as VhvV2Vorbelegungdaten;
      if (
        actionPayload.appId !== App.VHV_V2 ||
        actionPayload.angebotsId !== state?.angebotsId ||
        vorhandeneVorbelegungdaten?.forApp !== App.VHV_V2 ||
        vorhandeneVorbelegungdaten?.produktlinienname !== Produktlinienname.PREMIUM ||
        !state?.vertragsoptionen ||
        !selectIsErsatzgeschaeft(state)
      ) {
        return newSectionState;
      }
      return {
        ...newSectionState,
        produktlinien: preselectPremiumAsDefault(newSectionState?.produktlinien),
      };
    }
    case VhvActions.VERSSUMME_FAHRRAD_CHANGED: {
      const actionPayload: FahrradWert = action.payload;
      const newProduktLinien = newSectionState.produktlinien.map(produktlinie => {
        if (produktlinie.zusatzoptionen) {
          produktlinie.zusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
            if (
              zusatzoption.id === ZUSATZOPTION_FAHRRAD_ID ||
              zusatzoption.id === ZUSATZOPTION_FAHRRAD_KASKO_ID
            ) {
              return { ...zusatzoption, eigenschaft: actionPayload };
            }
            return zusatzoption;
          });
        }
        return produktlinie;
      });
      return { ...newSectionState, produktlinien: newProduktLinien };
    }
    default:
      return newSectionState;
  }
};

function preselectPremiumAsDefault(produktlinien: Produktlinie[]): Produktlinie[] {
  const premium = produktlinien?.find(
    produktlinie => produktlinie.produktName === Produktlinienname.PREMIUM,
  );
  if (!premium || premium.deactivated) {
    return produktlinien;
  }
  return produktlinien.map(produktlinie => ({
    ...produktlinie,
    selected: produktlinie.produktName === Produktlinienname.PREMIUM,
  }));
}
